import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { BuildingRow, DeptRow, FloorRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';
import { useAuthStore } from '../stores/auth';

const DEPT_TYPES = [
  { value: 'CLINICAL', label: '临床科室' },
  { value: 'ADMIN', label: '行政科室' },
  { value: 'LOGISTICS', label: '后勤科室' },
  { value: 'NURSING_UNIT', label: '护理单元' },
  { value: 'OPD_EMERG', label: '门诊/急诊' },
  { value: 'LANDMARK', label: '地标' }
];

/** 科室与空间（docs/05 §6-2）：科室树 + 楼宇楼层，读写均走 /api/sys/dept|building|floor。 */
export default function BasicDept() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage = profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'sys:org:manage') : false);

  const [tab, setTab] = useState('dept');
  const [deptEditing, setDeptEditing] = useState<DeptRow | null>(null);
  const [deptOpen, setDeptOpen] = useState(false);
  const [buildingEditing, setBuildingEditing] = useState<BuildingRow | null>(null);
  const [buildingOpen, setBuildingOpen] = useState(false);
  const [floorEditing, setFloorEditing] = useState<FloorRow | null>(null);
  const [floorOpen, setFloorOpen] = useState(false);
  const [deptForm] = Form.useForm();
  const [buildingForm] = Form.useForm();
  const [floorForm] = Form.useForm();

  const depts = useQuery({ queryKey: ['basic', 'dept'], queryFn: () => hq.orgStructure.dept.list({}) });
  const buildings = useQuery({ queryKey: ['basic', 'building'], queryFn: () => hq.orgStructure.building.list({}) });
  const floors = useQuery({ queryKey: ['basic', 'floor'], queryFn: () => hq.orgStructure.floor.list({}) });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['basic'] });
  };

  const saveDept = useMutation({
    mutationFn: () => hq.orgStructure.dept.save(deptForm.getFieldsValue()),
    onSuccess: async () => {
      message.success('科室已保存');
      setDeptOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const saveBuilding = useMutation({
    mutationFn: () => hq.orgStructure.building.save(buildingForm.getFieldsValue()),
    onSuccess: async () => {
      message.success('楼宇已保存');
      setBuildingOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const saveFloor = useMutation({
    mutationFn: () => hq.orgStructure.floor.save(floorForm.getFieldsValue()),
    onSuccess: async () => {
      message.success('楼层已保存');
      setFloorOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const remove = (kind: 'dept' | 'building' | 'floor', id: string, name: string, hasChildren: boolean) => {
    if (hasChildren) {
      message.warning('存在下级引用，不能删除');
      return;
    }
    modal.confirm({
      title: `删除${name}`,
      content: '删除为逻辑删除，历史单据快照不受影响。',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (kind === 'dept') await hq.orgStructure.dept.remove(id);
        if (kind === 'building') await hq.orgStructure.building.remove(id);
        if (kind === 'floor') await hq.orgStructure.floor.remove(id);
        message.success('已删除');
        await invalidate();
      }
    });
  };

  const deptColumns: ColumnsType<DeptRow> = [
    { title: '科室编码', dataIndex: 'deptCode', width: 130 },
    { title: '科室名称', dataIndex: 'deptName', width: 180, ellipsis: { showTitle: false }, render: ellipsisCell('deptName') },
    { title: '上级', dataIndex: 'parentName', width: 150, render: (value: string | null) => value || '-' },
    {
      title: '类型',
      dataIndex: 'deptType',
      width: 120,
      render: (value: string) => <Tag>{DEPT_TYPES.find((item) => item.value === value)?.label ?? value}</Tag>
    },
    { title: '位置', width: 180, render: (_, row) => [row.buildingName, row.floorName].filter(Boolean).join(' / ') || '-' },
    {
      title: '二级库',
      dataIndex: 'isSubStock',
      width: 90,
      render: (value: boolean) => (value ? <Tag color="blue">是</Tag> : '-')
    },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={!canManage}
            onClick={() => {
              setDeptEditing(row);
              deptForm.setFieldsValue({
                ...row,
                parentId: row.parentId === '0' ? undefined : row.parentId,
                buildingId: row.buildingId === '0' ? undefined : row.buildingId,
                floorId: row.floorId === '0' ? undefined : row.floorId
              });
              setDeptOpen(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" danger disabled={!canManage} onClick={() => remove('dept', row.id, row.deptName, row.childCount > 0)}>
            删除
          </Button>
        </Space>
      )
    }
  ];

  const buildingColumns: ColumnsType<BuildingRow> = [
    { title: '楼宇编码', dataIndex: 'buildingCode', width: 130 },
    { title: '楼宇名称', dataIndex: 'buildingName', width: 200 },
    { title: '院区', dataIndex: 'campusName', width: 160, render: (value: string | null) => value || '-' },
    { title: '楼层数', dataIndex: 'floorCount', width: 90, align: 'right' },
    { title: '已建楼层', dataIndex: 'floorRows', width: 100, align: 'right' },
    { title: '定位科室', dataIndex: 'deptCount', width: 100, align: 'right' },
    {
      title: '操作',
      width: 150,
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={!canManage}
            onClick={() => {
              setBuildingEditing(row);
              buildingForm.setFieldsValue(row);
              setBuildingOpen(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            disabled={!canManage}
            onClick={() => remove('building', row.id, row.buildingName, row.floorRows > 0 || row.deptCount > 0)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  const floorColumns: ColumnsType<FloorRow> = [
    { title: '楼层编码', dataIndex: 'floorCode', width: 120 },
    { title: '楼层名称', dataIndex: 'floorName', width: 160 },
    { title: '楼宇', dataIndex: 'buildingName', width: 180, render: (value: string | null) => value || '-' },
    { title: '楼层号', dataIndex: 'floorNo', width: 90, align: 'right' },
    { title: '科室数', dataIndex: 'deptCount', width: 90, align: 'right' },
    { title: '配送提示', dataIndex: 'deliveryRouteHint', ellipsis: { showTitle: false }, render: ellipsisCell('deliveryRouteHint') },
    {
      title: '操作',
      width: 150,
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={!canManage}
            onClick={() => {
              setFloorEditing(row);
              floorForm.setFieldsValue(row);
              setFloorOpen(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" danger disabled={!canManage} onClick={() => remove('floor', row.id, row.floorName, row.deptCount > 0)}>
            删除
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>科室与空间<Typography.Text type="secondary">科室树 / 楼宇 / 楼层（编辑需 sys:org:manage）</Typography.Text></Space>}
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => invalidate()} loading={depts.isFetching}>
            刷新
          </Button>
          {tab === 'dept' ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canManage}
              onClick={() => {
                setDeptEditing(null);
                deptForm.resetFields();
                deptForm.setFieldsValue({ deptType: 'CLINICAL', spaceNodeType: 'DEPT', status: 'ACTIVE', acceptDelivery: true, canCostBear: true });
                setDeptOpen(true);
              }}
            >
              新增科室
            </Button>
          ) : null}
          {tab === 'building' ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canManage}
              onClick={() => {
                setBuildingEditing(null);
                buildingForm.resetFields();
                buildingForm.setFieldsValue({ status: 'ACTIVE', floorCount: 1, sortNo: 0 });
                setBuildingOpen(true);
              }}
            >
              新增楼宇
            </Button>
          ) : null}
          {tab === 'floor' ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canManage}
              onClick={() => {
                setFloorEditing(null);
                floorForm.resetFields();
                floorForm.setFieldsValue({ status: 'ACTIVE', floorNo: 1, sortNo: 0 });
                setFloorOpen(true);
              }}
            >
              新增楼层
            </Button>
          ) : null}
        </Space>
      }
    >
      {!canManage ? (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="当前账号为只读：科室/楼宇/楼层的维护需要 sys:org:manage。" />
      ) : null}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'dept',
            label: `科室（${depts.data?.length ?? 0}）`,
            children: (
              <Table<DeptRow>
                rowKey="id"
                size="middle"
                columns={deptColumns}
                dataSource={depts.data ?? []}
                loading={depts.isPending}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: sumWidth(deptColumns) }}
              />
            )
          },
          {
            key: 'building',
            label: `楼宇（${buildings.data?.length ?? 0}）`,
            children: (
              <Table<BuildingRow>
                rowKey="id"
                size="middle"
                columns={buildingColumns}
                dataSource={buildings.data ?? []}
                loading={buildings.isPending}
                pagination={false}
                scroll={{ x: sumWidth(buildingColumns) }}
              />
            )
          },
          {
            key: 'floor',
            label: `楼层（${floors.data?.length ?? 0}）`,
            children: (
              <Table<FloorRow>
                rowKey="id"
                size="middle"
                columns={floorColumns}
                dataSource={floors.data ?? []}
                loading={floors.isPending}
                pagination={false}
                scroll={{ x: sumWidth(floorColumns) }}
              />
            )
          }
        ]}
      />

      <Modal
        title={deptEditing ? '编辑科室' : '新增科室'}
        open={deptOpen}
        onCancel={() => setDeptOpen(false)}
        onOk={() => deptForm.submit()}
        confirmLoading={saveDept.isPending}
        width={640}
        destroyOnHidden
      >
        <Form form={deptForm} layout="vertical" onFinish={() => saveDept.mutate()} preserve={false}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="deptCode" label="科室编码" rules={[{ required: true }]} style={{ width: 220 }}>
              <Input placeholder="如 KS-001" maxLength={32} />
            </Form.Item>
            <Form.Item name="deptName" label="科室名称" rules={[{ required: true }]} style={{ width: 300 }}>
              <Input maxLength={128} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="deptType" label="科室类型" style={{ width: 220 }}>
              <Select options={DEPT_TYPES} />
            </Form.Item>
            <Form.Item name="parentId" label="上级科室" style={{ width: 300 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(depts.data ?? []).filter((item) => item.id !== deptEditing?.id).map((item) => ({ value: item.id, label: item.deptName }))}
              />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="buildingId" label="楼宇" style={{ width: 220 }}>
              <Select allowClear showSearch optionFilterProp="label" options={(buildings.data ?? []).map((item) => ({ value: item.id, label: item.buildingName }))} />
            </Form.Item>
            <Form.Item name="floorId" label="楼层" style={{ width: 300 }}>
              <Select allowClear showSearch optionFilterProp="label" options={(floors.data ?? []).map((item) => ({ value: item.id, label: item.floorName }))} />
            </Form.Item>
          </Space>
          <Space size={24}>
            <Form.Item name="isSubStock" label="科室二级库" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="acceptDelivery" label="可收货" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="canCostBear" label="可承担成本" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }} options={[{ value: 'ACTIVE', label: '启用' }, { value: 'INACTIVE', label: '停用' }]} />
            </Form.Item>
          </Space>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={buildingEditing ? '编辑楼宇' : '新增楼宇'}
        open={buildingOpen}
        onCancel={() => setBuildingOpen(false)}
        onOk={() => buildingForm.submit()}
        confirmLoading={saveBuilding.isPending}
        destroyOnHidden
      >
        <Form form={buildingForm} layout="vertical" onFinish={() => saveBuilding.mutate()} preserve={false}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="buildingCode" label="楼宇编码" rules={[{ required: true }]} style={{ width: 220 }}>
              <Input maxLength={32} />
            </Form.Item>
            <Form.Item name="buildingName" label="楼宇名称" rules={[{ required: true }]} style={{ width: 300 }}>
              <Input maxLength={128} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="campusName" label="院区" style={{ width: 220 }}>
              <Input maxLength={128} />
            </Form.Item>
            <Form.Item name="floorCount" label="楼层数" style={{ width: 140 }}>
              <InputNumber min={1} max={200} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select options={[{ value: 'ACTIVE', label: '启用' }, { value: 'INACTIVE', label: '停用' }]} />
            </Form.Item>
          </Space>
          <Form.Item name="address" label="地址">
            <Input maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={floorEditing ? '编辑楼层' : '新增楼层'}
        open={floorOpen}
        onCancel={() => setFloorOpen(false)}
        onOk={() => floorForm.submit()}
        confirmLoading={saveFloor.isPending}
        destroyOnHidden
      >
        <Form form={floorForm} layout="vertical" onFinish={() => saveFloor.mutate()} preserve={false}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="buildingId" label="所属楼宇" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={(buildings.data ?? []).map((item) => ({ value: item.id, label: item.buildingName }))} />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="floorCode" label="楼层编码" rules={[{ required: true }]} style={{ width: 220 }}>
              <Input maxLength={32} />
            </Form.Item>
            <Form.Item name="floorName" label="楼层名称" rules={[{ required: true }]} style={{ width: 300 }}>
              <Input maxLength={64} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="floorNo" label="楼层号（负数=地下）" style={{ width: 200 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select options={[{ value: 'ACTIVE', label: '启用' }, { value: 'INACTIVE', label: '停用' }]} />
            </Form.Item>
          </Space>
          <Form.Item name="deliveryRouteHint" label="配送提示">
            <Input maxLength={255} placeholder="如：住院部东电梯上 5 楼左转" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
